<template>
  <div class="package-detail">
    <!-- Tombol Kembali -->
    <router-link :to="{ name: 'AdminDashboard' }" class="back-link">&larr; Kembali ke Paket Tes</router-link>

    <!-- Loading State -->
    <div v-if="isLoading">Memuat data...</div>

    <!-- Utama: Jika Data Sudah Ada -->
    <div v-else-if="packageData" class="main-content">
      <!-- Header dengan Mode Edit -->
      <header class="page-header">
        <h1>{{ isEditing ? 'Edit Paket Tes' : packageData.name }}</h1>
        <button @click="toggleEditMode" class="action-btn primary small">
          {{ isEditing ? 'Batal' : 'Edit Paket' }}
        </button>
      </header>

      <!-- MODE EDIT: Form Edit Paket -->
      <div v-if="isEditing" class="card edit-form">
        <form @submit.prevent="saveChanges"> <!-- TAMBAHKAN INI -->
          <div class="form-group">
            <label>Nama Paket</label>
            <input v-model="editablePackage.name" required />
          </div>
          <div class="form-group">
            <label>Kode Akses</label>
            <input v-model="editablePackage.access_code" required />
          </div>
          <div class="form-group">
            <label>Template Psikogram</label>
            <select v-model="editablePackage.psychogram_template_id">
              <option :value="null">-- Tanpa Template --</option>
              <option v-for="template in psychogramTemplates" :key="template.id" :value="template.id">
                {{ template.name }}
              </option>
            </select>
          </div>
          <div class="form-group checkbox">
            <label>
              <input v-model="editablePackage.is_active" type="checkbox" />
              Aktif
            </label>
          </div>
          <div class="form-actions">
            <button type="submit" :disabled="isSavingChanges" class="primary">
              {{ isSavingChanges ? 'Menyimpan...' : 'Simpan Perubahan' }}
            </button>
            <button type="button" @click="isEditing = false" :disabled="isSavingChanges">
              Batal
            </button>
          </div>
        </form>
      </div>

      <!-- MODE LIHAT: Detail Paket -->
      <div v-else>
        <!-- Informasi Paket -->
        <div class="header-meta">
          <span><strong>Kode Akses:</strong> <code>{{ packageData.access_code }}</code></span>
          <span><strong>Status:</strong> 
            <span class="badge" :class="{ active: packageData.is_active, inactive: !packageData.is_active }">
              {{ packageData.is_active ? 'Aktif' : 'Tidak Aktif' }}
            </span>
          </span>
          <span>
            Template: 
            <strong v-if="packageData.psychogram_template">
              {{ packageData.psychogram_template.name }}
            </strong>
            <em v-else>
              - (Tidak terhubung ke template)
            </em>
          </span>
        </div>

        <!-- Daftar Tes dalam Paket -->
        <div class="card">
          <h2>Tes dalam Paket Ini 
            <small v-if="isSavingOrder">(Menyimpan urutan...)</small>
          </h2>
          <p class="instruction">Tarik dan lepas untuk mengubah urutan tes.</p>
          <draggable v-if="packageData.tests && packageData.tests.length > 0"
            v-model="packageData.tests"
            item-key="id"
            tag="ul"
            class="test-list draggable"
            @end="onDragEnd"
          >
            <template #item="{ element: test }">
              <li class="test-item draggable-item">
                <div class="test-info">
                  <span>{{ test.name }}</span>
                  <small>Durasi: {{ test.duration_minutes }} menit</small>
                </div>
                <button @click="removeTestFromPackage(test.id)" class="remove-btn">Hapus</button>
              </li>
            </template>
          </draggable>

          <p v-else class="empty-state">Belum ada tes di dalam paket ini.</p>
        </div>

        <!-- Tambah Tes ke Paket -->
        <div class="card">
          <h2>Tambahkan Tes ke Paket</h2>
          <div v-if="availableTests.length > 0" class="add-test-form">
            <select v-model="selectedTestId">
              <option disabled value="">Pilih tes untuk ditambahkan</option>
              <option v-for="test in availableTests" :key="test.id" :value="test.id">
                {{ test.name }} ({{ test.duration_minutes }} menit)
              </option>
            </select>
            <button @click="addTestToPackage" :disabled="!selectedTestId" class="add-btn">
              Tambahkan
            </button>
          </div>
          <p v-else class="empty-state">Semua master tes sudah ada di dalam paket ini.</p>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import apiClient from '@/services/api.js';
import draggable from 'vuedraggable';

// --- STATE ---
const route = useRoute();
const packageId = route.params.id;

const packageData = ref(null);
const masterTests = ref([]);
const psychogramTemplates = ref([]);

const selectedTestId = ref('');
const isLoading = ref(true);
const error = ref('');

const isEditing = ref(false);
const isSavingChanges = ref(false);

const editablePackage = ref({
  name: '',
  access_code: '',
  is_active: true,
  psychogram_template_id: null
});

const isSavingOrder = ref(false);

// --- FETCH DATA ---
const fetchPackageDetail = async () => {
  try {
    console.log(`Fetching detail for package ID: ${packageId}`); // DEBUG
    const response = await apiClient.get(`/admin/packages/${packageId}`);
    console.log("Package detail received:", response.data); // DEBUG
    packageData.value = response.data;
  } catch (err) {
    error.value = 'Gagal memuat detail paket.';
    console.error("Error fetching package detail:", err.response); // DEBUG
  }
};

const fetchMasterTests = async () => {
  try {
    const response = await apiClient.get('/admin/tests/master-list?limit=1000');
    masterTests.value = response.data.tests;
  } catch (err) {
    console.error("Gagal memuat daftar master tes:", err);
  }
};

const fetchPsychogramTemplates = async () => {
  try {
    const response = await apiClient.get('/admin/psychogram-templates/');
    psychogramTemplates.value = response.data;
  } catch (err) {
    console.error("Gagal memuat template psikogram:", err);
  }
};

// --- TOGGLE EDIT MODE ---
const toggleEditMode = () => {
  isEditing.value = !isEditing.value;
  if (isEditing.value && packageData.value) {
    // Salin data ke form edit
    editablePackage.value = { ...packageData.value };
  }
};

// --- SIMPAN PERUBAHAN PAKET ---
const saveChanges = async () => {
  isSavingChanges.value = true;
  try {
    await apiClient.put(`/admin/packages/${packageId}`, editablePackage.value);
    await fetchPackageDetail(); // Muat ulang data
    isEditing.value = false;
    alert("Perubahan berhasil disimpan.");
  } catch (err) {
    alert("Gagal menyimpan perubahan.");
    console.error(err);
  } finally {
    isSavingChanges.value = false;
  }
};

// --- ATUR URUTAN TES ---
const onDragEnd = async () => {
  // packageData.value.tests sekarang sudah dalam urutan baru berkat v-model
  if (!packageData.value) return;
  
  isSavingOrder.value = true; // Tampilkan pesan "Menyimpan..."
  try {
    // Buat array yang hanya berisi ID tes dalam urutan yang baru
    const testIdsInOrder = packageData.value.tests.map(t => t.id);
    
    // Panggil API baru untuk menyimpan urutan
    const response = await apiClient.put(
      `/admin/packages/${packageId}/test-order`, 
      { test_ids: testIdsInOrder }
    );

    // Update data lokal dengan respons dari server untuk memastikan konsistensi
    packageData.value = response.data;
    alert("Urutan tes berhasil disimpan.");
  } catch (error) {
    alert("Gagal menyimpan urutan tes. Halaman akan dimuat ulang.");
    console.error(error);
    // Muat ulang data untuk mengembalikan ke urutan semula jika gagal
    await fetchPackageDetail();
  } finally {
    isSavingOrder.value = false;
  }
};

// --- TAMBAHKAN TES KE PAKET ---
const addTestToPackage = async () => {
  if (!selectedTestId.value) return;

  try {
    await apiClient.post(`/admin/packages/${packageId}/tests/${selectedTestId.value}`);
    selectedTestId.value = '';
    await fetchPackageDetail();
  } catch (err) {
    alert('Gagal menambahkan tes.');
    console.error(err);
  }
};

// --- HAPUS TES DARI PAKET ---
const removeTestFromPackage = async (testId) => {
  if (!window.confirm("Yakin ingin menghapus tes ini dari paket?")) return;

  try {
    await apiClient.delete(`/admin/packages/${packageId}/tests/${testId}`);
    await fetchPackageDetail();
  } catch (err) {
    alert("Gagal menghapus tes dari paket.");
    console.error(err);
  }
};

const availableTests = computed(() => {
  // Pengaman Utama: Jika salah satu dari data penting belum siap,
  // kembalikan array kosong untuk mencegah error.
  if (!packageData.value || !packageData.value.tests || !Array.isArray(masterTests.value)) {
    return [];
  }
  
  // Logika Anda yang sudah benar
  const existingTestIds = new Set(packageData.value.tests.map(t => t.id));
  return masterTests.value.filter(t => !existingTestIds.has(t.id));
});


// --- MOUNTED ---
onMounted(async () => {
  await Promise.all([
    fetchPackageDetail(),
    fetchMasterTests(),
    fetchPsychogramTemplates()
  ]);
  isLoading.value = false;
});
</script>

<style scoped>
.package-detail {
  max-width: 960px;
  margin: auto;
  padding: 30px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #333;
}

.back-link {
  display: inline-block;
  margin-bottom: 20px;
  color: #3498db;
  text-decoration: none;
  font-size: 0.95em;
  transition: color 0.2s ease;
}
.back-link:hover {
  color: #2980b9;
  text-decoration: underline;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* --- HEADER --- */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-header h1 {
  margin: 0;
  font-size: 1.8em;
  color: #2c3e50;
  font-weight: 600;
}

.action-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 0.95em;
  transition: all 0.2s ease;
}

.action-btn.primary {
  background-color: #1abc9c;
  color: white;
  border-color: #1abc9c;
}

.action-btn.small {
  padding: 6px 12px;
  font-size: 0.9em;
}

/* --- HEADER META INFO --- */
.header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
  font-size: 0.95em;
  color: #555;
}

.header-meta span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-meta code {
  background-color: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
  color: white;
}

.badge.active {
  background-color: #28a745;
}

.badge.inactive {
  background-color: #dc3545;
}

/* --- CARD STYLING --- */
.card {
  background-color: #fff;
  padding: 28px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  border: 1px solid #eee;
}

.card h2 {
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 1.4em;
  font-weight: 600;
}

.card p {
  margin: 0;
  font-size: 0.95em;
  color: #666;
}

.instruction {
  font-size: 0.9em;
  font-style: italic;
  color: #555;
  margin-bottom: 16px;
}

.empty-state {
  text-align: center;
  color: #999;
  font-style: italic;
  padding: 20px;
}

/* --- FORM EDIT PAKET --- */
.edit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-weight: 500;
  color: #555;
  font-size: 0.9em;
}

.form-group input,
.form-group select {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1em;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #1abc9c;
  box-shadow: 0 0 0 2px rgba(26, 188, 156, 0.2);
}

.form-group.checkbox {
  grid-column: span 2;
}

.form-group.checkbox label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
}

.form-group.checkbox input {
  width: 18px;
  height: 18px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  justify-content: flex-end;
}

.form-actions button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  cursor: pointer;
  transition: background-color 0.2s;
}

.form-actions button:first-child {
  background-color: #1abc9c;
  color: white;
}

.form-actions button:first-child:hover:not(:disabled) {
  background-color: #16a085;
}

.form-actions button:last-child {
  background-color: #6c757d;
  color: white;
}

.form-actions button:last-child:hover {
  background-color: #5a6268;
}

.form-actions button:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

/* --- DAFTAR TES (DRAGGABLE) --- */
.test-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.test-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: #fafafa;
  transition: transform 0.2s, box-shadow 0.2s;
}

.test-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.test-info {
  flex-grow: 1;
}

.test-info span {
  font-weight: 500;
  color: #333;
}

.test-info small {
  color: #7f8c8d;
  font-size: 0.9em;
}

.remove-btn {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  cursor: pointer;
}

.remove-btn:hover {
  background-color: #c0392b;
}

/* --- FORM TAMBAH TES --- */
.add-test-form {
  display: flex;
  gap: 12px;
  align-items: end;
}

.add-test-form select {
  flex-grow: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1em;
}

.add-test-form select:focus {
  outline: none;
  border-color: #1abc9c;
  box-shadow: 0 0 0 2px rgba(26, 188, 156, 0.2);
}

.add-btn {
  padding: 12px 16px;
  background-color: #1abc9c;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  cursor: pointer;
  transition: background-color 0.2s;
}

.add-btn:hover:not(:disabled) {
  background-color: #16a085;
}

.add-btn:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

/* --- DRAG HANDLER --- */
.draggable-item {
  cursor: grab;
}

.draggable-item:active {
  cursor: grabbing;
}

/* --- ERROR MESSAGE --- */
.error {
  color: #e74c3c;
  font-size: 0.9em;
  margin-top: 15px;
  padding: 10px;
  background-color: #fdf2f2;
  border-left: 4px solid #e74c3c;
  border-radius: 4px;
}

/* --- RESPONSIVE --- */
@media (max-width: 768px) {
  .package-detail {
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-btn {
    align-self: stretch;
    margin-top: 10px;
  }

  .edit-grid {
    grid-template-columns: 1fr;
  }

  .add-test-form {
    flex-direction: column;
  }

  .form-actions {
    justify-content: center;
  }

  .form-actions button {
    width: 100%;
  }
}
</style>